import { Position, useStoreApi } from '@xyflow/react'
import dagre from '@dagrejs/dagre'
import { useCallback } from 'react'
import { cloneDeep, reverse } from 'lodash'
import type { GenericNode, GenericEdge } from '../types'

interface LayoutOptions {
  nodeWidth?: number
  nodeHeight?: number
}

interface LayoutResult {
  nodes: GenericNode[]
  edges: GenericEdge[]
}

export function useNodeLayout() {
  const store = useStoreApi<GenericNode, GenericEdge>()
  const getLayoutedElements = useCallback(
    (nodes: GenericNode[], edges: GenericEdge[], options: LayoutOptions = {}): LayoutResult => {
      const { nodeWidth = 292, nodeHeight = 200 } = options

      const dagreGraph = new dagre.graphlib.Graph()
      dagreGraph.setDefaultEdgeLabel(() => ({}))
      dagreGraph.setGraph({ rankdir: 'TB', ranksep: 140, edgesep: 100, nodesep: 180 })

      // Add nodes to dagre
      nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: node.measured?.width, height: node.measured?.height })
      })

      const reversedEdges = reverse(cloneDeep(edges))
      // Add edges to dagre
      reversedEdges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target)
      })

      // Calculate layout
      dagre.layout(dagreGraph)

      // Transform the nodes with new positions
      const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id)

        return {
          ...node,
          targetPosition: Position.Top,
          sourcePosition: Position.Bottom,
          position: {
            x: nodeWithPosition.x - nodeWidth / 2,
            y: nodeWithPosition.y - nodeHeight / 2,
          },
        }
      })

      return {
        nodes: layoutedNodes,
        edges,
      }
    },
    [],
  )

  const layoutNodes = useCallback(
    (options?: LayoutOptions) => {
      const { nodes, edges } = store.getState()
      return getLayoutedElements(nodes, edges, options)
    },
    [getLayoutedElements, store],
  )

  return {
    layoutNodes,
    getLayoutedElements,
  }
}
